import React, { useEffect, useState } from "react";
import moment from "moment";
import { GetOrderTransaction } from "../../utils/ApiConstant";
import useHttpHook from "../../Hooks/HttpHook";
import { getPaidOrRemaining } from "../../utils/Helper";
import MainPagination from "../Common/MainPagination";
import { CancelEmiModal } from "./CancelEmiModal";

const EmiInformation = ({ details }) => {
  const [loading, sendRequest, error] = useHttpHook();
  const [transactions, setTransactions] = useState(null);
  const [showCancelEmiModal, setShowCancelEmiModal] = useState(false);

  const getTransactions = async (page = 1) => {
    if (!details?._id) {
      return;
    }
    try {
      const response = await sendRequest(
        `${GetOrderTransaction}${details._id}?page=${page}&limit=4`,
        { method: "get" }
      );
      if (response.status === 1) {
        setTransactions(response);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const { paid, remaining } = getPaidOrRemaining(details);
  const installmentTotal = Number(details?.emiInfo?.installment || 0);
  const installmentPaid = Number(details?.emiInfo?.paid || 0);
  const orderTotal = Number(details?.total || 0);
  const paidAmount = Number(paid);
  const progressPct =
    orderTotal > 0
      ? Math.min(100, Math.round((paidAmount / orderTotal) * 100))
      : installmentTotal > 0
        ? Math.min(100, Math.round((installmentPaid / installmentTotal) * 100))
        : 0;

  useEffect(() => {
    getTransactions();
  }, [details?._id]);

  const items = transactions?.data?.itemsList || [];
  const successfulTransactions = items.filter(
    (transaction) => String(transaction.status).toLowerCase() === "success"
  );
  const firstPaidTransaction = [...successfulTransactions].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  )[0];
  const hasPaidInstallment = installmentPaid > 0 || paidAmount > 0;
  const firstPaymentDate = hasPaidInstallment
    ? firstPaidTransaction?.createdAt ||
      details?.emiInfo?.firstPaymentDate ||
      details?.createdAt
    : null;

  return (
    <section className="event-order-emi" aria-label="Split payment information">
      <div className="row g-4">
        <div className="col-md-6">
          <div className="event-order-emi-card">
            <div className="event-order-emi-total">
              <span className="event-order-emi-label">Total Amount</span>
              <strong className="event-order-emi-amount">
                USD {parseFloat(details?.total || 0).toFixed(2)}
              </strong>
            </div>

            <div
              className="event-order-emi-progress"
              role="progressbar"
              aria-valuenow={progressPct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Payment progress"
            >
              <div
                className="event-order-emi-progress-bar"
                style={{ width: `${progressPct}%` }}
              />
            </div>

            <div className="event-order-emi-status">
              <div className="event-order-emi-status-item">
                <span className="event-order-emi-status-label is-paid">
                  <i className="event-order-emi-dot is-paid" aria-hidden="true" />
                  Paid
                </span>
                <strong>USD {paid}</strong>
              </div>
              <div className="event-order-emi-status-item">
                <span className="event-order-emi-status-label is-remaining">
                  <i
                    className="event-order-emi-dot is-remaining"
                    aria-hidden="true"
                  />
                  Remaining
                </span>
                <strong>USD {remaining}</strong>
              </div>
            </div>

            <dl className="event-order-emi-meta">
              <div className="event-order-emi-meta-item">
                <dt>First Payment date</dt>
                <dd>
                  {firstPaymentDate
                    ? moment(firstPaymentDate).format("DD/MM/YYYY")
                    : "—"}
                </dd>
              </div>
              <div className="event-order-emi-meta-item">
                <dt>Duration</dt>
                <dd>
                  {installmentTotal || 0}{" "}
                  {details?.emiInfo?.interval === "day" ? "Days" : "Months"}
                </dd>
              </div>
              <div className="event-order-emi-meta-item">
                <dt>Paid</dt>
                <dd>{installmentPaid}</dd>
              </div>
              <div className="event-order-emi-meta-item">
                <dt>Pending</dt>
                <dd>{Number(details?.emiInfo?.remaining || 0)}</dd>
              </div>
            </dl>
          </div>
        </div>

        <div className="col-md-6">
          <div className="event-order-emi-transactions">
            <h3 className="event-order-emi-transactions-title">
              Recent Transactions
            </h3>

            {loading && !transactions ? (
              <p className="event-order-emi-empty">Loading transactions…</p>
            ) : items.length > 0 ? (
              <MainPagination
                getEventsMethod={getTransactions}
                MainData={transactions}
                PaginationType="paginate"
              >
                <div className="event-order-emi-table-wrap">
                  <table
                    className="event-order-emi-table"
                    aria-label="Recent split payment transactions"
                  >
                    <thead>
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col">Amount</th>
                        <th scope="col">Created At</th>
                        <th scope="col">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((transaction, index) => {
                        const rowNumber =
                          (transactions?.data?.paginator?.slNo || 1) + index;
                        const status = String(
                          transaction.status || "pending"
                        ).toLowerCase();

                        return (
                          <tr key={transaction._id || index}>
                            <td className="event-order-emi-cell-id">
                              {rowNumber}
                            </td>
                            <td className="event-order-emi-cell-amount">
                              USD{" "}
                              {parseFloat(transaction.amount || 0).toFixed(2)}
                            </td>
                            <td className="event-order-emi-cell-date">
                              {moment(transaction.createdAt).format(
                                "DD/MM/YYYY"
                              )}
                            </td>
                            <td className="event-order-emi-cell-status">
                              <span
                                className={`event-order-emi-status-badge is-${status}`}
                              >
                                {transaction.status || "pending"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </MainPagination>
            ) : (
              <div className="event-order-emi-empty-state">
                <i className="bi bi-receipt" aria-hidden="true" />
                <p>{error || "No transactions yet."}</p>
                <span>
                  Split payments will show here after each installment is charged.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <CancelEmiModal
        show={showCancelEmiModal}
        orderId={details?._id}
        onClose={() => setShowCancelEmiModal(false)}
      />
    </section>
  );
};

export default EmiInformation;
